import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import EmployeeDataService from "../../services/Employee";
import {
  LeaveBalanceData,
  leaveTypeLabel,
  sortedLeaveTypes,
} from "./leaveBalanceFormat";
import { SingleRowEmployeeLeave } from "../../components/List/SingleRowEmployeeLeave";
import { DateTime } from "luxon";
import { CalendarIcon, InfoIcon } from "@chakra-ui/icons";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { HolidayCalendarModal } from "../../components/Hrms/HolidayCalendarModal";
import { leaveTypeColor } from "../../components/Hrms/hrmsUi";
import {
  LeavePolicyModal,
  LeavePolicyType,
} from "../../components/Hrms/LeavePolicyModal";
import {
  CompOffForm,
  GrantCompOffModal,
} from "../../components/Hrms/GrantCompOffModal";
import { getUserAccess } from "../../utils/authUtils";

export const EmployeeLeavesList = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const toast = useToast();
  const localPagesize = localStorage.getItem("assessment-list-pageSize-content",);
  const newlocalPagesize = localPagesize ? JSON.parse(localPagesize) : 7;

  const [isLoading, setIsLoading] = useState(false);

  const {
    isOpen: isImageOpen,
    onOpen: onImageOpen,
    onClose: onImageClose,
  } = useDisclosure();

  const {
    isOpen: isPolicyOpen,
    onOpen: onPolicyOpen,
    onClose: onPolicyClose,
  } = useDisclosure();

  const navigate = useNavigate();
  const [data, setData] = useState<any>([]);

  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(newlocalPagesize);
  const [totalPages, setTotalPages] = useState(0);

  const [myBalance, setMyBalance] = useState<LeaveBalanceData | null>(null);
  // Opens on the current month; the dropdown index counts months from
  // November 2025, matching generateMonthYearArray().
  const now = DateTime.now();
  const [month, setMonth] = useState(now.toFormat("MM"));
  const [year, setYear] = useState(String(now.year));
  const [date, setDate] = useState("");
  const [selectedValue, setSelectedValue] = useState(
    String((now.year - 2025) * 12 + now.month - 11),
  );

  // Holiday Master (Configuration - Admin epic)
  const [holidays, setHolidays] = useState<any[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeavePolicyType[]>([]);

  // Compensatory Off state
  const [compOffs, setCompOffs] = useState<any[]>([]);
  const {
    isOpen: isCompOffOpen,
    onOpen: onCompOffOpen,
    onClose: onCompOffClose,
  } = useDisclosure();
  const emptyCompOff: CompOffForm = { email: "", date: "", reason: "" };
  const [compOffData, setCompOffData] = useState<CompOffForm>(emptyCompOff);
  const [employees, setEmployees] = useState<{ email: string; name?: string }[]>([]);
  const { canManageEmployees } = getUserAccess();


  const user = localStorage.getItem("logged_in_email");
  const fetchMyBalance = async () => {
    try {
      const res = await EmployeeDataService.getLeaveTypeBalance(token, user || "");
      if (res?.data?.status === "success") setMyBalance(res.data.data);
    } catch (e: any) {
      console.error("Error fetching leave balance:", e);
    }
  };

  const generateMonthYearArray = () => {
    try {
      const startDate = DateTime.fromObject({
        month: 11,
        year: 2025,
      });
      const endDate = DateTime.now();
      let currentDate = startDate;
      const monthYearArray = [];
      let i = 0;

      while (currentDate <= endDate) {
        monthYearArray.push({
          month: currentDate.toFormat("MM"),
          year: currentDate.year,
          text: `${currentDate.toFormat("MMMM")}-${currentDate.year}`,
          index: i.toString(),
        });
        currentDate = currentDate.plus({ months: 1 });
        i++;
      }

      return monthYearArray;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  // "My Leaves" always shows only the logged-in user's own history — team
  // visibility lives in "Leave Approvals" (pending) and the Manager
  // Dashboard's team leave requests widget (all statuses) instead.
  const fetchData = () => {
    setIsLoading(true);
    EmployeeDataService.getAllEmployeeLeaves(
      token,
      user,
      user,
      month,
      year,
      date,
    )
      .then((res) => {
        const data = res.data.data;
        setData(data);
        setTotal(res.data.total);
        fetchMyBalance();
        // setTotalPages(res.data.pagination.totalPages);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        if (e.response.status === 401) {
          toast({
            position: "top",
            title: "Authentication Failed.",
            description: "Your session is expired.",
            status: "error",
            duration: 500,
            isClosable: true,
          });
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }
        console.log(e);
      });
  };

  const fetchHolidays = () => {
    EmployeeDataService.getHolidays(token)
      .then((res) => {
        if (res.data.status === "success") {
          setHolidays(res.data.data || []);
        }
      })
      .catch((e) => console.log(e));
  };

  const fetchCompOffs = () => {
    EmployeeDataService.getCompensatoryOffs(token, user || "", "")
      .then((res) => {
        if (res.data?.data) {
          setCompOffs(res.data.data);
        }
      })
      .catch((e) => console.log(e));
  };

  const handleGrantCompOff = () => {
    if (!compOffData.email || !compOffData.date || !compOffData.reason) {
      toast({
        position: "top",
        title: "Please fill all fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const employee = employees.find((emp) => emp.email === compOffData.email);
    EmployeeDataService.grantCompensatoryOff(
      token,
      { ...compOffData, name: employee?.name || "" },
      user || "",
    )
      .then(() => {
        toast({
          position: "top",
          title: "Compensatory Off granted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onCompOffClose();
        setCompOffData(emptyCompOff);
        fetchCompOffs();
        fetchMyBalance();
      })
      .catch((e: any) => {
        toast({
          position: "top",
          title: "Failed to grant Comp-Off",
          description: e.response?.data?.message || "Something went wrong",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const handleSelection = (value: any) => {
    setDate("");

    setSelectedValue(value);

    const selectedMonth = generateMonthYearArray().find(
      (el) => el.index === value,
    );

    // "All months" has no entry: clear the month so every leave is listed.
    setMonth(selectedMonth ? selectedMonth.month : "");
    setYear(selectedMonth ? selectedMonth.year.toString() : "");
  };

  useEffect(() => {
    fetchData();
    fetchHolidays();
    EmployeeDataService.getLeaveTypes(token)
      .then((res) => {
        if (res.data.status === "success") setLeaveTypes(res.data.data || []);
      })
      .catch((e) => console.log(e));
    fetchCompOffs();
    if (canManageEmployees) {
      EmployeeDataService.getAllEmployeeProfiles(token, user || "")
        .then((res) => setEmployees(res.data?.data || []))
        .catch((e) => console.log(e));
    }
  }, []);

  const balanceCards = myBalance
    ? sortedLeaveTypes(myBalance.balances).map((code) => {
        const balance = myBalance.balances[code];
        const eligible = myBalance.eligibility?.[code]?.eligible !== false;
        return { code, balance, eligible, reason: myBalance.eligibility?.[code]?.reason };
      })
    : [];

  return (
    <HrmsPageShell
      title="My Leaves"
      description="Your leave balance and leave history"
      sectionKey="leave"
      actions={
        <>
          <Button size="sm" variant="outline" leftIcon={<InfoIcon />} onClick={onPolicyOpen}>
            Leave Policy
          </Button>
          <Button size="sm" variant="outline" leftIcon={<CalendarIcon />} onClick={onImageOpen}>
            Holiday Calendar
          </Button>
          {canManageEmployees && (
            <Button size="sm" variant="outline" onClick={onCompOffOpen}>
              Grant Comp-Off
            </Button>
          )}
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={() => navigate("/hrms/leaves/apply")}
          >
            Apply for Leave
          </Button>
        </>
      }
    >
      <HolidayCalendarModal
        isOpen={isImageOpen}
        onClose={onImageClose}
        holidays={holidays}
      />
      <GrantCompOffModal
        isOpen={isCompOffOpen}
        onClose={onCompOffClose}
        value={compOffData}
        onChange={setCompOffData}
        onSubmit={handleGrantCompOff}
        employees={employees}
      />
      <LeavePolicyModal
        isOpen={isPolicyOpen}
        onClose={onPolicyClose}
        leaveTypes={leaveTypes}
        balance={myBalance}
        holidays={holidays}
        onViewHolidays={() => {
          onPolicyClose();
          onImageOpen();
        }}
      />

      {balanceCards.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={5}>
          {balanceCards.map(({ code, balance, eligible, reason }) => {
            const color = leaveTypeColor(code);
            const unlimited = balance.annual === -1;
            return (
              <Box
                key={code}
                bg="white"
                border="1px solid #e2e8f0"
                borderTop="3px solid"
                borderTopColor={`${color}.400`}
                borderRadius="12px"
                px={4}
                py={3}
                title={eligible ? undefined : reason}
              >
                <Text fontSize="12px" fontWeight="700" color="gray.600" textTransform="uppercase" m={0}>
                  {leaveTypeLabel(code)}
                </Text>
                {!eligible ? (
                  <Text fontSize="14px" fontWeight="600" color="gray.400" m={0} mt={2}>
                    Not eligible yet
                  </Text>
                ) : unlimited ? (
                  <>
                    <Text fontSize="22px" fontWeight="800" color={`${color}.600`} m={0} lineHeight="1.3">
                      {balance.used}
                    </Text>
                    <Text fontSize="11px" color="gray.500" m={0}>
                      used · no limit
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontSize="22px" fontWeight="800" color={balance.remaining === 0 ? "red.500" : `${color}.600`} m={0} lineHeight="1.3">
                      {balance.remaining}
                      <Text as="span" fontSize="12px" fontWeight="500" color="gray.500">
                        {" "}left
                      </Text>
                    </Text>
                    <Progress
                      value={balance.annual > 0 ? (balance.used / balance.annual) * 100 : 0}
                      size="xs"
                      borderRadius="full"
                      colorScheme={color}
                      my={1.5}
                    />
                    <Text fontSize="11px" color="gray.500" m={0}>
                      {balance.used} of {balance.annual} used
                    </Text>
                  </>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      <Flex justify="space-between" align="flex-end" gap={3} mb={3} wrap="wrap">
        <Text fontSize="15px" fontWeight="700" color="#1d4747" m={0}>
          Leave history
        </Text>
        <Flex align="flex-end" gap={2} wrap="wrap">
          <Select
            size="sm"
            w="180px"
            bg="white"
            borderRadius="8px"
            placeholder="All months"
            onChange={(e) => handleSelection(e.target.value)}
            value={selectedValue}
          >
            {generateMonthYearArray().map((el) => (
              <option key={el.index} value={el.index}>
                {el.text}
              </option>
            ))}
          </Select>
          <Input
            size="sm"
            w="160px"
            bg="white"
            borderRadius="8px"
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSelectedValue("");
              setMonth("");
              setYear("");
            }}
          />
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            px={5}
            onClick={() => fetchData()}
            isLoading={isLoading}
          >
            Apply
          </Button>
        </Flex>
      </Flex>

      <HrmsTable
        head={["Type", "Dates", "Reason", "Status", "Remark"]}
        isEmpty={data.length === 0}
        emptyText="No leave records for this period"
      >
        {data.map((el: any) => (
          <SingleRowEmployeeLeave
            key={el._id}
            id={el._id}
            data={el}
            func={fetchData}
          />
        ))}
      </HrmsTable>

      {/* Admins see every grant; everyone else sees their own. */}
      <Text fontSize="15px" fontWeight="700" color="#1d4747" mt={6} mb={3}>
        Comp-off grants
      </Text>
      <HrmsTable
        head={[
          ...(canManageEmployees ? ["Employee"] : []),
          "Date worked",
          "Reason",
          "Granted by",
        ]}
        isEmpty={compOffs.length === 0}
        emptyText="No comp-off grants"
      >
        {compOffs.map((grant: any) => (
          <tr key={grant._id}>
            {canManageEmployees && <td>{grant.name || grant.email}</td>}
            <td>{DateTime.fromISO(grant.date).toFormat("dd LLL yyyy")}</td>
            <td>{grant.reason}</td>
            <td>{grant.grantedBy}</td>
          </tr>
        ))}
      </HrmsTable>
    </HrmsPageShell>
  );
};
